import type { ContentType } from "@brookbush/database";

import type { PortableTextBlock as PortableTextBlockType } from "@portabletext/types";
import z from "zod";

import type { ExamDifficulty } from "./exams.types";
import type { UserAction } from "./user.types";

export type DeepRequired<T> = {
	[P in keyof T]-?: T[P] extends (infer U)[]
		? DeepRequired<U>[]
		: T[P] extends object
			? DeepRequired<T[P]>
			: NonNullable<T[P]>;
};

export enum Access {
	PAID = "paid",
	FREE = "free",
}

export interface Thumbnail {
	url: string;
	alt?: string;
}
export type Logger = (...args: any[]) => void;

export const nullable = <T extends z.ZodTypeAny>(schema: T) =>
	z.union([schema, z.null()]);

export interface Author {
	_id: string;
	fullName: string;
	firstName?: string;
	lastName?: string;
	honorific?: string;
	thumbnail: Thumbnail;
}

export interface TutorialStep {
	content: string;
	target: string;
	disableScrolling?: boolean;
}

export type PortableTextBlock = PortableTextBlockType;

export type ContentItem = {
	id: number;

	createdAt: Date;
	updatedAt: Date;
	userId?: string;

	contentId: string;
	content?: Content;
};

export interface Content {
	_type: ContentType;
	_id: string;
	title: string;
	slug: string;
	thumbnail?: Thumbnail;
	credits?: number;
	duration?: number;
	finalExamDifficulty?: ExamDifficulty;
}

export enum Direction {
	FORWARD = "forward",
	BACKWARD = "backward",
}

export interface AudioWithChapters {
	audio: Audio;
	chapters: Chapter[];
	currentTimestamp?: number;
}

export interface Chapter {
	_key: string;
	title: string;
	audioTimestamp: number;
}

export interface Audio {
	title: string;
	url: string;
	_id: string;
	/** Sanity URL when `url` was swapped for an R2 mirror URL. */
	fallbackUrl?: string;
}

export interface PDFLink {
	viewLink: string;
	downloadLink: string;
}

export interface TrackingHeaders {
	posthogSessionId?: string;
}

export const json = z.object({}).catchall(z.string());

export const MILLISECOND = 1;
export const SECOND = 1000 * MILLISECOND;
export const MINUTE = 60 * SECOND;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;
export const WEEK = 7 * DAY;

export const TIMEZONE = "America/New_York";

export const CONVERSION_MODAL_CONFIG = {
	baseDelayMs: 5 * MINUTE,
	multiplier: 2,
	maxShows: 6,
	ttl: 30 * DAY,
} as const;

export interface ConversionModalState {
	lastShown: number; // Unix timestamp (ms)
	count: number; // Number of times shown
}

export interface CtaOverrides {
	hasContextualSignUp?: boolean;
	contextualSignUp?: ContextualCTA;
	hasContextualMembership?: boolean;
	contextualMembership?: ContextualCTA;
}

export interface ContextualCTA {
	title?: string;
	description?: string;
	buttonText?: string;
	mobileButtonText?: string;
	enableMedia?: string;
	image?: string;
	video?: string;
	redirectButton?: boolean;
	buttonRedirectURL?: string;
	userAction: UserAction;
}
